import { useEffect, useState } from 'react';
import { adminApi } from '../../api/client';
import { Check } from 'lucide-react';

const inputClass = 'w-full px-3.5 py-2.5 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring';

const SECTIONS = [
  {
    title: 'Verzending',
    fields: [
      { key: 'shippingCost', label: 'Verzendkosten (EUR)', hint: '0 = altijd gratis', type: 'number', step: '0.01' },
      { key: 'freeShippingThreshold', label: 'Gratis verzending vanaf (EUR incl. BTW)', hint: '0 = altijd verzendkosten', type: 'number', step: '1' },
    ],
  },
  {
    title: 'B2B kortingen',
    fields: [
      { key: 'b2bDiscountThreshold', label: 'B2B kortingsdrempel (EUR excl. BTW)', hint: 'Min. subtotaal voor B2B standaard korting', type: 'number', step: '1' },
      { key: 'b2bDiscountRate', label: 'B2B kortingspercentage (%)', hint: 'Toegepast wanneer drempel bereikt', type: 'number', step: '0.1' },
    ],
  },
  {
    title: 'Facturatiegegevens',
    fields: [
      { key: 'companyLegalName', label: 'Bedrijfsnaam', type: 'text' },
      { key: 'companyAddress', label: 'Adres', type: 'text' },
      { key: 'companyVatNumber', label: 'BTW-nummer', type: 'text' },
      { key: 'companyBankAccount', label: 'Bankrekening', type: 'text' },
      { key: 'invoiceFooterNote', label: 'Factuur voetnoot', hint: 'Betalingstermijn o.i.d.', type: 'text' },
    ],
  },
  {
    title: 'E-mail instellingen',
    fields: [
      { key: 'senderName', label: 'Afzendernaam', hint: 'Weergavenaam in e-mails', type: 'text' },
      { key: 'replyToEmail', label: 'Antwoord-adres', hint: 'Reply-to e-mailadres (optioneel)', type: 'text' },
    ],
  },
];

export default function AdminConfig() {
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    adminApi.getConfig().then(r => {
      const map = {};
      r.data.forEach(c => { map[c.key] = c.value; });
      setConfigs(map);
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    await adminApi.updateConfig(configs);
    setSaved(true);
    setSaving(false);
    setTimeout(() => setSaved(false), 2000);
  };

  if (loading) return <div className="p-8"><div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" /></div>;

  return (
    <div data-testid="admin-config-page" className="p-8 max-w-2xl">
      <h1 className="font-serif text-3xl font-light text-foreground mb-2">Configuratie</h1>
      <p className="text-sm text-muted-foreground mb-8">Commerce- en factuurinstellingen die de backend-waarheid beinvloeden.</p>

      {SECTIONS.map(section => (
        <div key={section.title} className="bg-card border border-border rounded-2xl p-6 space-y-5 shadow-sm mb-6">
          <h2 className="font-serif text-base font-medium text-foreground">{section.title}</h2>
          {section.fields.map(f => (
            <div key={f.key}>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {f.label}
                {f.hint && <span className="ml-1.5 text-xs text-muted-foreground font-normal">{f.hint}</span>}
              </label>
              <input
                data-testid={`config-${f.key}`}
                type={f.type || 'text'}
                step={f.step}
                value={configs[f.key] || ''}
                onChange={e => setConfigs(c => ({ ...c, [f.key]: e.target.value }))}
                className={inputClass}
              />
            </div>
          ))}
        </div>
      ))}

      <button
        data-testid="save-config-btn"
        onClick={handleSave}
        disabled={saving}
        className={`inline-flex items-center gap-2 rounded-full px-8 py-3 text-sm font-semibold transition-all active:scale-95 shadow-sm disabled:opacity-50 ${saved ? 'bg-green-600 text-white' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}
      >
        {saved ? <><Check className="w-4 h-4" /> Opgeslagen</> : saving ? 'Opslaan...' : 'Opslaan'}
      </button>
    </div>
  );
}
